import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";

function formatNumber(n) {
  if (n === null || n === undefined || n === "") return "0";
  const v = typeof n === "number" ? n : parseInt(n, 10);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

function formatDuration(ms) {
  if (!ms || ms < 0) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s - m * 60);
  return `${m}m ${rs}s`;
}

function copyJson(obj) {
  try {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
  } catch {
    /* no-op */
  }
}

function CollapsibleBlock({ title, defaultOpen = false, stat, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="prompt-section" style={{ marginBottom: "0.75rem" }}>
      <div
        className="section-header"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((v) => !v)}
      >
        <h4>{open ? "▾" : "▸"} {title}</h4>
        {stat && <span className="section-stat">{stat}</span>}
      </div>
      {open && children}
    </div>
  );
}

function ActionResultCard({ entry, idx }) {
  const ok = entry.success !== false && !entry.error;
  return (
    <div className={`variable-item ${ok ? "" : "empty"}`} style={{ marginBottom: "0.5rem" }}>
      <div className="variable-name">
        <span className="variable-label">
          {ok ? "✅" : "❌"} #{idx + 1} · {entry.action || "(unknown)"}
        </span>
      </div>
      {entry.args && Object.keys(entry.args).length > 0 && (
        <>
          <div className="variable-stats">args</div>
          <pre className="variable-value">{JSON.stringify(entry.args, null, 2)}</pre>
        </>
      )}
      {entry.result !== undefined && entry.result !== null && (
        <>
          <div className="variable-stats">result</div>
          <pre className="variable-value">
            {typeof entry.result === "string"
              ? entry.result
              : JSON.stringify(entry.result, null, 2)}
          </pre>
        </>
      )}
      {entry.error && (
        <>
          <div className="variable-stats" style={{ color: "#c33" }}>error</div>
          <pre className="variable-value" style={{ color: "#c33" }}>{entry.error}</pre>
        </>
      )}
    </div>
  );
}

function PassCard({ pass }) {
  const stat = [
    `pass #${pass.passNumber}`,
    pass.trigger,
    pass.llmLatencyMs != null ? `${pass.llmLatencyMs} ms` : null,
    pass.actionCount != null ? `${pass.actionCount} action(s)` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const results = pass.actionsResults || [];

  return (
    <div
      className="prompt-log-detail"
      style={{ border: "1px solid #ccd", borderRadius: 6, padding: "0.75rem", marginBottom: "0.75rem" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <strong>Pass #{pass.passNumber} — {pass.trigger}</strong>
        <span className="section-stat">{stat}</span>
      </div>

      <CollapsibleBlock
        title="Input messages"
        stat={`${(pass.inputMessages || []).length} message(s)`}
      >
        <pre className="prompt-content">
          {JSON.stringify(pass.inputMessages || [], null, 2)}
        </pre>
      </CollapsibleBlock>

      <CollapsibleBlock
        title="Raw response"
        defaultOpen={true}
        stat={`${formatNumber((pass.rawResponse || "").length)} chars`}
      >
        <pre className="prompt-content">{pass.rawResponse || "(empty)"}</pre>
      </CollapsibleBlock>

      <CollapsibleBlock
        title={`Action results (${results.length})`}
        defaultOpen={results.length > 0}
      >
        {results.length === 0 ? (
          <p className="empty-section">no actions in this pass</p>
        ) : (
          results.map((r, i) => <ActionResultCard key={i} entry={r} idx={i} />)
        )}
      </CollapsibleBlock>
    </div>
  );
}

function RoutineExecutions() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextKey, setNextKey] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [hydrated, setHydrated] = useState({});
  const [hydrating, setHydrating] = useState({});
  const [routineFilter, setRoutineFilter] = useState("");

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const qs = routineFilter
        ? `/routine-runs?limit=10&routineId=${encodeURIComponent(routineFilter)}`
        : "/routine-runs?limit=10";
      const data = await apiClient.get(qs);
      setRuns(data.items || []);
      setNextKey(data.nextKey || null);
    } catch (err) {
      console.error("Failed to fetch routine runs:", err);
    } finally {
      setLoading(false);
    }
  }, [routineFilter]);

  const loadMore = useCallback(async () => {
    if (!nextKey || loadingMore) return;
    setLoadingMore(true);
    try {
      let qs = `/routine-runs?limit=10&startKey=${encodeURIComponent(nextKey)}`;
      if (routineFilter) qs += `&routineId=${encodeURIComponent(routineFilter)}`;
      const data = await apiClient.get(qs);
      setRuns((prev) => [...prev, ...(data.items || [])]);
      setNextKey(data.nextKey || null);
    } catch (err) {
      console.error("Failed to load more routine runs:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [nextKey, loadingMore, routineFilter]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const toggleExpand = async (run) => {
    if (expandedId === run.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(run.id);

    const needsHydration = run.spillover && (run.spillover.bucket || run.spillover.key);
    if (needsHydration && !hydrated[run.id] && !hydrating[run.id]) {
      setHydrating((h) => ({ ...h, [run.id]: true }));
      try {
        const data = await apiClient.get(`/routine-runs?id=${encodeURIComponent(run.id)}`);
        if (data && data.item) {
          setHydrated((h) => ({ ...h, [run.id]: data.item }));
        }
      } catch (err) {
        console.error("Failed to hydrate routine run:", err);
      } finally {
        setHydrating((h) => ({ ...h, [run.id]: false }));
      }
    }
  };

  const uniqueRoutines = Array.from(
    new Map(runs.map((r) => [r.routineId, r.routineName || r.routineId])).entries(),
  ).filter(([id]) => id);

  return (
    <div className="output-analysis">
      <p className="prompt-analysis-description">
        Per-execution log of every routine Anna runs (scheduled or manual). Captures
        the system prompt, full live context (memories, schedule, skills),
        every LLM pass, every action result, and which notifications were sent.
        Items larger than ~350 KB spill to S3 and are hydrated on click.
      </p>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", alignItems: "center" }}>
        <label className="variable-label">Filter:</label>
        <select
          value={routineFilter}
          onChange={(e) => setRoutineFilter(e.target.value)}
          style={{ padding: "0.25rem 0.5rem" }}
        >
          <option value="">All routines</option>
          {uniqueRoutines.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <button type="button" className="section-tab" onClick={fetchRuns}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="placeholder">Loading routine runs...</div>
      ) : runs.length === 0 ? (
        <div className="placeholder">
          No routine executions recorded yet. Wait for a scheduled run or trigger
          one manually from Scheduled Tasks.
        </div>
      ) : (
        <div className="prompt-logs-list">
          {runs.map((run) => {
            const isExpanded = expandedId === run.id;
            const full = hydrated[run.id] || run;
            const passes = full.passes || [];
            const passCount = full.passCount || passes.length;
            const isError = run.status === "error";

            return (
              <div key={run.id} className={`prompt-log-item ${isExpanded ? "expanded" : ""}`}>
                <div className="prompt-log-header" onClick={() => toggleExpand(run)}>
                  <div className="prompt-log-meta">
                    <span className="prompt-log-source">
                      {isError ? "❌" : "✅"} {run.trigger}
                    </span>
                    <span className="prompt-log-time">
                      {run.startedAt ? new Date(run.startedAt + "Z").toLocaleString() : "—"}
                    </span>
                    <span className="prompt-log-session" title={run.routineId}>
                      {run.routineName || run.routineId}
                    </span>
                  </div>
                  <div className="prompt-log-preview">
                    {(run.finalResponsePreview || run.finalResponse || "").slice(0, 100)}
                    {(run.finalResponsePreview || run.finalResponse || "").length > 100 ? "..." : ""}
                  </div>
                  <div className="prompt-log-tokens">
                    <span className="token-badge total">
                      {passCount} pass{passCount === 1 ? "" : "es"}
                    </span>
                    {run.actionCount > 0 && (
                      <span className="token-badge chat">
                        {run.actionCount} action{run.actionCount > 1 ? "s" : ""}
                      </span>
                    )}
                    <span className="token-badge skills">{formatDuration(run.durationMs)}</span>
                    {run.convergenceReason && (
                      <span className="token-badge skills">{run.convergenceReason}</span>
                    )}
                    {run.spillover?.bucket && (
                      <span className="token-badge user" title={run.spillover.key}>S3</span>
                    )}
                    {isError && (
                      <span className="token-badge user" style={{ color: "#c33" }}>error</span>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="prompt-log-detail">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                      <span className="section-stat">
                        {hydrating[run.id]
                          ? "Hydrating from S3…"
                          : `${passCount} pass(es) · ${formatDuration(run.durationMs)} · convergence: ${full.convergenceReason || "—"}`}
                      </span>
                      <button type="button" className="section-tab" onClick={() => copyJson(full)}>
                        Copy as JSON
                      </button>
                    </div>

                    {full.error && (
                      <CollapsibleBlock
                        title={`Error (${full.errorType || "Exception"})`}
                        defaultOpen={true}
                      >
                        <pre className="prompt-content" style={{ color: "#c33" }}>{full.error}</pre>
                      </CollapsibleBlock>
                    )}

                    <CollapsibleBlock
                      title="Routine prompt"
                      defaultOpen={true}
                      stat={`${formatNumber((full.routinePrompt || "").length)} chars`}
                    >
                      <pre className="prompt-content">
                        {full.routinePrompt || "(none — see scheduled task)"}
                      </pre>
                    </CollapsibleBlock>

                    <CollapsibleBlock
                      title="Final response"
                      defaultOpen={true}
                      stat={`${formatNumber((full.finalResponse || full.finalResponsePreview || "").length)} chars`}
                    >
                      <pre className="prompt-content">
                        {full.finalResponse || full.finalResponsePreview || "(empty)"}
                      </pre>
                    </CollapsibleBlock>

                    {passes.length > 0 && (
                      <CollapsibleBlock
                        title={`LLM passes (${passes.length})`}
                        defaultOpen={true}
                      >
                        {passes.map((p, i) => <PassCard key={i} pass={p} />)}
                      </CollapsibleBlock>
                    )}

                    <CollapsibleBlock
                      title={`Notifications (${(full.notifications || []).length})`}
                      defaultOpen={(full.notifications || []).length > 0}
                    >
                      {(full.notifications || []).length === 0 ? (
                        <p className="empty-section">no notifications sent</p>
                      ) : (
                        full.notifications.map((n, i) => (
                          <div key={i} className="variable-item" style={{ marginBottom: "0.4rem" }}>
                            <div className="variable-name">
                              <span className="variable-label">
                                {n.status === "sent" ? "✅" : n.status === "error" ? "❌" : "⏭️"}
                                {" "}{n.channel}
                                {n.recipient ? ` → ${n.recipient}` : ""}
                                {n.chatId ? ` → chat ${n.chatId}` : ""}
                              </span>
                              <code className="variable-key">{n.status}</code>
                            </div>
                            {n.error && (
                              <pre className="variable-value" style={{ color: "#c33" }}>{n.error}</pre>
                            )}
                            {n.reason && (
                              <pre className="variable-value">{n.reason}</pre>
                            )}
                          </div>
                        ))
                      )}
                    </CollapsibleBlock>

                    <CollapsibleBlock title="Live context snapshot">
                      <pre className="prompt-content">
                        {JSON.stringify(full.context || {}, null, 2)}
                      </pre>
                    </CollapsibleBlock>

                    <CollapsibleBlock
                      title="System prompt (full)"
                      stat={`${formatNumber((full.systemPrompt || "").length)} chars`}
                    >
                      <pre className="prompt-content">{full.systemPrompt || "(empty)"}</pre>
                    </CollapsibleBlock>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && nextKey && (
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <button
            type="button"
            className="section-tab"
            onClick={loadMore}
            disabled={loadingMore}
            style={{ padding: "0.4rem 1.2rem" }}
          >
            {loadingMore ? "Loading…" : "Load 10 more"}
          </button>
        </div>
      )}
    </div>
  );
}

export default RoutineExecutions;
